import { Link } from "react-router-dom";
import { ShoppingBag, Heart, User as UserIcon, LogOut, Menu, Package, ShieldCheck, Store, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import Logo, { LogoMark } from "./Logo";
import OrderNotificationBell from "./OrderNotificationBell";
import SearchBar from "./SearchBar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "./ui/dropdown-menu";

export default function Header() {
  const { user, loginWithGoogle, logout } = useAuth();
  const { count } = useCart();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-200">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 h-16 flex items-center gap-2 sm:gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
          {/* Compact mark on mobile, full wordmark from md up */}
          <span className="md:hidden"><LogoMark size={28} /></span>
          <span className="hidden md:flex"><Logo size={32} /></span>
        </Link>

        <nav className="hidden md:flex items-center gap-5 text-sm">
          <Link to="/products" data-testid="nav-shop" className="hover:text-[#0033FF] transition-colors">Shop</Link>
          <Link to="/products?sort=rating" data-testid="nav-bestsellers" className="hover:text-[#0033FF] transition-colors">Bestsellers</Link>
          <Link to="/products?sort=newest" data-testid="nav-new" className="hover:text-[#0033FF] transition-colors">New</Link>
        </nav>

        <SearchBar />

        <div className="flex items-center gap-1">
          <Button asChild variant="ghost" size="icon" data-testid="wishlist-link">
            <Link to="/wishlist"><Heart className="w-5 h-5" /></Link>
          </Button>
          <Button asChild variant="ghost" size="icon" data-testid="cart-link" className="relative">
            <Link to="/cart">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#0033FF] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">
                  {count}
                </span>
              )}
            </Link>
          </Button>

          <OrderNotificationBell />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <UserIcon className="w-5 h-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none border-black">
                <DropdownMenuLabel className="font-display">
                  <div className="text-sm font-bold">{user.name}</div>
                  <div className="text-xs text-neutral-500 font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-orders">
                  <Link to="/orders"><Package className="w-4 h-4 mr-2" />My Orders</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-account">
                  <Link to="/account"><UserIcon className="w-4 h-4 mr-2" />Account</Link>
                </DropdownMenuItem>
                {(user.role === "seller" || user.role === "admin") && (
                  <DropdownMenuItem asChild data-testid="menu-affiliate">
                    <Link to="/affiliate"><Sparkles className="w-4 h-4 mr-2" />Earn (Affiliate)</Link>
                  </DropdownMenuItem>
                )}
                {(user.role === "seller" || user.role === "admin") && (
                  <DropdownMenuItem asChild data-testid="menu-seller">
                    <Link to="/seller"><Store className="w-4 h-4 mr-2" />Seller Dashboard</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild data-testid="menu-admin">
                    <Link to="/admin"><ShieldCheck className="w-4 h-4 mr-2" />Admin</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem asChild data-testid="menu-become-seller">
                    <Link to="/seller/apply" className="group">
                      <Store className="w-4 h-4 mr-2 text-[#00A355]" />
                      <span className="flex-1">
                        <span className="block font-semibold">Earn cash with us</span>
                        <span className="block text-[10px] uppercase tracking-widest text-neutral-500 group-hover:text-[#00A355]">Become a seller →</span>
                      </span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="login-btn"
              onClick={loginWithGoogle}
              className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-10 px-3 sm:px-5 text-sm font-semibold"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
